/**
 * AddAccountOwnerModal - Moneta-admin action to seed an organization's first AccountOwner.
 *
 * Collects the owner's email + a temporary password and calls POST /users/account-owner
 * (Moneta-gated). The created user gets a full owner role set (AccountOwner + OrgAdmin +
 * UserAdmin + Analyst) so they can immediately run their org, and is created in Cognito with
 * a temporary password (forced change on first login).
 *
 * @param {object|null} org - Target org ({ organization, ... }). Null hides the modal.
 * @param {function} onClose - Dismiss the modal.
 * @param {function} onAdded - Called after a successful create (receives the owner email).
 */
import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Pressable, ActivityIndicator, TextInput, Modal } from 'react-native';
import { Feather } from '@expo/vector-icons';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import Util from '../../util/util';
import { useTranslation } from '../../i18n';

// Pull a readable message out of a thrown API error (Amplify rejects non-2xx with e.g. `409 {"error":"..."}`)
const extractApiError = (err, fallback) => {
  const message = err?.message || String(err || '');
  const match = message.match(/\{.*"error".*\}/);
  if (match) {
    try {
      const parsed = JSON.parse(match[0]);
      if (parsed?.error) return parsed.error;
    } catch {
      // fall through
    }
  }
  return message || fallback;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddAccountOwnerModal = ({ org, onClose, onAdded }) => {
  const { t } = useTranslation(['admin', 'common']);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const orgName = org?.organization;

  useEffect(() => {
    // Reset the form whenever a new org is targeted
    if (orgName) {
      setEmail('');
      setPassword('');
      setError(null);
      setSubmitting(false);
    }
  }, [orgName]);

  const emailValid = EMAIL_REGEX.test(email.trim());
  const passwordValid = password.length >= 8;
  const canSubmit = emailValid && passwordValid && !submitting;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await Util.post('UserManagement', '/users/account-owner', {
        body: {
          organization: orgName,
          email: email.trim().toLowerCase(),
          password,
        },
      });
      if (response?.error) {
        setError(response.error);
        return;
      }
      onAdded(email.trim().toLowerCase());
    } catch (err) {
      console.error('Error adding account owner:', err);
      setError(extractApiError(err, t('accountOwner.addFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal visible={!!org} transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable style={styles.overlay} onPress={handleClose}>
        <Pressable style={styles.modal} onPress={(e) => e.stopPropagation()}>
          <View style={styles.header}>
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
              {t('accountOwner.title')}
            </CustomText>
            <Pressable
              onPress={handleClose}
              style={({ hovered, focused }) => [
                styles.closeButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.closeButtonHovered,
              ]}
              accessibilityLabel={t('accountOwner.closeLabel')}
            >
              <Feather name="x" size={20} color={AppStyles.colorSet.textSecondary} />
            </Pressable>
          </View>

          <CustomText size={14} color={AppStyles.colorSet.textSecondary} style={styles.subtitle}>
            {t('accountOwner.subtitle', { organization: orgName })}
          </CustomText>

          {/* Email */}
          <CustomText size={13} color={AppStyles.colorSet.textSecondary} style={styles.label}>
            {t('accountOwner.emailLabel')}
          </CustomText>
          <TextInput
            style={[styles.input, email.length > 0 && !emailValid && styles.inputError]}
            value={email}
            onChangeText={setEmail}
            placeholder={t('accountOwner.emailPlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textMuted}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            editable={!submitting}
          />

          {/* Temporary password */}
          <CustomText size={13} color={AppStyles.colorSet.textSecondary} style={styles.label}>
            {t('accountOwner.passwordLabel')}
          </CustomText>
          <TextInput
            style={[styles.input, password.length > 0 && !passwordValid && styles.inputError]}
            value={password}
            onChangeText={setPassword}
            placeholder={t('accountOwner.passwordPlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textMuted}
            autoCapitalize="none"
            autoCorrect={false}
            secureTextEntry
            editable={!submitting}
          />
          <CustomText size={12} color={AppStyles.colorSet.textMuted} style={styles.hint}>
            {t('accountOwner.passwordHint')}
          </CustomText>

          {error && (
            <View style={styles.errorBox}>
              <Feather name="alert-circle" size={16} color="#DC2626" />
              <CustomText size={13} color="#DC2626" style={styles.errorText}>
                {error}
              </CustomText>
            </View>
          )}

          <View style={styles.buttonRow}>
            <Pressable
              style={({ hovered, focused, pressed }) => [
                styles.cancelButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.cancelButtonHovered,
                pressed && { opacity: 0.7 },
              ]}
              onPress={handleClose}
              disabled={submitting}
            >
              <CustomText size={14} color={AppStyles.colorSet.textSecondary}>
                {t('common:actions.cancel')}
              </CustomText>
            </Pressable>
            <Pressable
              style={({ hovered, focused }) => [
                styles.submitButton,
                { cursor: 'pointer' },
                canSubmit && (hovered || focused) && styles.submitButtonHovered,
                !canSubmit && styles.submitButtonDisabled,
              ]}
              onPress={handleSubmit}
              disabled={!canSubmit}
              accessibilityLabel={t('accountOwner.createLabel')}
            >
              {submitting ? (
                <ActivityIndicator size="small" color={AppStyles.colorSet.textPrimary} />
              ) : (
                <>
                  <Feather name="user-plus" size={16} color={AppStyles.colorSet.textPrimary} />
                  <CustomText size={14} font="medium" color={AppStyles.colorSet.textPrimary} style={{ marginLeft: 6 }}>
                    {t('accountOwner.addOwner')}
                  </CustomText>
                </>
              )}
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    width: '100%',
    maxWidth: 440,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  closeButton: {
    padding: 4,
    borderRadius: 8,
  },
  closeButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  subtitle: {
    marginBottom: 16,
    lineHeight: 20,
  },
  label: {
    marginBottom: 6,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
    marginBottom: 12,
  },
  inputError: {
    borderColor: AppStyles.colorSet.error,
  },
  hint: {
    marginTop: -6,
    marginBottom: 8,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: 8,
    padding: 12,
    marginTop: 4,
    gap: 8,
  },
  errorText: {
    flex: 1,
    lineHeight: 18,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    gap: 12,
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  cancelButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  submitButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
});

export default AddAccountOwnerModal;
